import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import {CdkDragDrop, CdkDragEnter, transferArrayItem} from '@angular/cdk/drag-drop';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable, throwError} from 'rxjs';
import {catchError, filter, finalize, first, map, switchMap, take, tap} from 'rxjs/operators';

import {HeaderSort, ModalService, NotificationService, TableComponent} from '@shared';

import {FeatureFlags, FeatureFlagService} from '@core';

import {
    ActiveEmployeeService,
    DragDropService,
    EmployeeService,
    EmployeeView,
    ManualRouteInformation,
    NavigationService,
    ProblemInstanceService,
    RouteKeyNumbers,
    RoutePlanningService,
    RoutesResult,
    RoutesService,
    TaskIdInfo,
    TaskValidateService,
    TaskView,
    Visit,
    VisitList,
    VisitMapperService
} from 'planner-shared';

import {RouteTaskView} from '../tasks/route-task-view.model';
import {RouteTaskService} from '../tasks/services/route-task.service';
import {RouteDelayService} from "../tasks/services/route-delay.service";
import {ShiftDelay} from "../tasks/services/models/employee-delay.model";
import {DelayLevel} from "../models/delay-level.model";
import {EmployeeSwapModalComponent} from "./swapping-modal/employee-swap-modal.component";

@UntilDestroy()
@Component({
    selector: 'app-route-planning-employees',
    templateUrl: './route-planning-employees.component.html',
    styleUrls: ['./route-planning-employees.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class RoutePlanningEmployeesComponent implements OnInit, OnDestroy {
    private readonly dragDropService = inject(DragDropService);
    private readonly employeeService = inject(EmployeeService);
    private readonly routesService = inject(RoutesService);
    private readonly routeTaskService = inject(RouteTaskService);
    private readonly routePlanningService = inject(RoutePlanningService);
    private readonly activeEmployeeService = inject(ActiveEmployeeService);
    private readonly notificationService = inject(NotificationService);
    private readonly taskValidateService = inject(TaskValidateService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly visitMapper = inject(VisitMapperService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly routeDelayService = inject(RouteDelayService);
    private readonly modalService = inject(ModalService);
    private readonly navigationService = inject(NavigationService);

    public displayEmployees: EmployeeView[];
    public employees: EmployeeView[];
    public selectedEmployee: EmployeeView;
    public totalActiveEmployees: number;
    public tableHoverEffectDisabled: boolean;
    public loading: boolean;
    public updating: boolean;
    public draggingTask: boolean;
    public swapWorklistsFeature: Observable<boolean>;
    public showRoster: boolean;
    public rideSharingInRoute: boolean;
    public swapModeActive = false;
    public selectedSwapEmployees = new Set<EmployeeView>();
    public employeeDelays: ShiftDelay[];
    public readonly maxSwapEmployees = 2;
    public highlightDelaysEnabled: boolean;
    public independentSyncTasksFeatureEnabled: boolean;

    @Output() public select = new EventEmitter<EmployeeView>();
    @Output() public reloadRoutes = new EventEmitter<VisitList>();

    @ViewChild(TableComponent, {static: true}) private readonly tableComponent: TableComponent;
    private sortValue: HeaderSort;
    private visits: Visit[];
    private keyNumbers: RouteKeyNumbers[];
    private newSearchPhrase: string;
    private isDragAndDropFixEnabled: boolean;

    private readonly InitSortBy = 'employeeName';
    protected readonly DelayLevel = DelayLevel;

    constructor() {
        this.totalActiveEmployees = 0;
    }

    ngOnInit(): void {
        this.loading = true;

        this.featureFlagService.isEnabled(FeatureFlags.LastStepDragFix)
            .subscribe(value => this.isDragAndDropFixEnabled = value);
        this.swapWorklistsFeature = this.featureFlagService.isEnabled(FeatureFlags.SwapWorklists);
        this.employeeService.getActiveEmployeesCount().subscribe(count => (this.totalActiveEmployees = count));

        this.tableComponent.headerSortService
            .getHeaderSort()
            .pipe(untilDestroyed(this))
            .subscribe(sortValue => {
                this.sortValue = sortValue;
                this.loadEmployees();
            });

        this.featureFlagService.isEnabled(FeatureFlags.HighlightDelays)
            .pipe(
                take(1),
                filter(isEnabled => isEnabled),
                tap(value => this.highlightDelaysEnabled = value),
                switchMap(() => this.routeDelayService.getRouteDelays()),
                untilDestroyed(this),
                tap(employeeDelays => {
                    this.employeeDelays = employeeDelays;
                    if (employeeDelays.length > 0) {
                        this.loadEmployees();
                    }
                })
            )
            .subscribe();

        this.featureFlagService.isEnabled(FeatureFlags.IndependentSyncTasks)
            .pipe(
                untilDestroyed(this),
                take(1),
                filter(isEnabled => isEnabled),
                tap(value => {
                    this.independentSyncTasksFeatureEnabled = value;
                    if (value) {
                        this.estimateSyncTaskAllocation();
                    }
                }),
                switchMap(() => this.routesService.getManualRouteChangeListener()),
                tap(() => {
                    this.estimateSyncTaskAllocation();
                })
            ).subscribe();

        this.routePlanningService
            .getEmployeeTaskUpdateStateChange()
            .pipe(untilDestroyed(this))
            .subscribe(updating => (this.updating = updating));

        this.dragDropService
            .draggingTask()
            .pipe(untilDestroyed(this))
            .subscribe(task => {
                this.draggingTask = task !== null;
                if (this.draggingTask) {
                    this.employees.forEach(originalEmployee => {
                        this.routeTaskService
                            .mapToRouteTasks(this.visits)
                            .pipe(
                                map(tasks => tasks.filter(i => i.workHourId === originalEmployee.workHourId)),
                                switchMap(tasks =>
                                    this.activeEmployeeService
                                        .mapToActiveEmployeeIncludeVisitRestrictions(originalEmployee, tasks)
                                        .pipe(
                                            switchMap(activeEmployee =>
                                                this.taskValidateService.isTaskValidForEmployee(activeEmployee, task)
                                            ),
                                            tap(taskValid => {
                                                originalEmployee.dropDisabled = !taskValid;
                                            })
                                        )
                                )
                            )
                            .subscribe();
                    });
                    this.displayEmployees = this.employees.filter(originalEmployee => !originalEmployee.dropDisabled);
                } else {
                    this.displayEmployees = [...this.employees];
                }
                if (this.isDragAndDropFixEnabled) {
                    this.changeDetectorRef.detectChanges();
                }
                this.toggleDropBox(task);
            });
        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .pipe()
            .subscribe(result => this.showRoster = result.valueOf());

        this.employeeService
            .employeeUpdateConfirm$
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.changeDetectorRef.detectChanges();
            });
    }

    private toggleDropBox(task: TaskView): void {
        if (task !== null) {
            document.querySelector('#drop-box').classList.remove('invisible');
        } else {
            setTimeout(() => {
                document.querySelector('#drop-box').classList.add('invisible');
            }, 300);

        }
    }

    ngOnDestroy(): void {
        this.deselectEmployee();
    }

    public init(visits: Visit[], keyNumbers: RouteKeyNumbers[]): void {
        this.visits = visits;
        this.keyNumbers = keyNumbers;
        this.rideSharingInRoute = this.keyNumbers.some(keyNumbers => keyNumbers.rideSharing);
        this.tableComponent.headerSortService.resort(new HeaderSort(this.InitSortBy, true));
    }

    public searchTextChange(searchPhrase: string): void {
        this.newSearchPhrase = searchPhrase;
        this.deselectEmployee();
        this.loadEmployees(searchPhrase);
    }

    public selectEmployee(employee: EmployeeView): void {
        if (this.updating || employee.selected) {
            return;
        }

        this.deselectEmployee();
        employee.selected = true;
        this.selectedEmployee = employee;
        this.select.emit(employee);
        this.activeEmployeeService.setActiveEmployee(employee);
    }

    public taskDropped(event: CdkDragDrop<TaskIdInfo[], RouteTaskView[]>, employee: EmployeeView): void {
        if (employee.dropDisabled) {
            if (event.item.data.isEmployeeTask && event.item.data.employeeId !== employee.employeeId) {
                this.notificationService.warning('CAN_NOT_MOVE_EMPLOYEE_SPECIFIC_TASK');
            } else if (!event.container.data.includes(event.item.data.originalTaskId)) {
                this.notificationService.warning('TASK_CAN_NOT_BE_MOVED_TO_EMPLOYEE');
            }
            this.dragDropService.setDraggingDisabled(false);
            return;
        }
        this.taskDroppedMrp(event, employee);
    }

    public taskDroppedMrp(event: CdkDragDrop<TaskIdInfo[], RouteTaskView[]>, employee: EmployeeView): void {
        event.previousContainer.data.splice(event.previousIndex, 1);

        const task = <TaskIdInfo>{
            taskId: event.item.data.originalTaskId,
            routePlannerTaskId: event.item.data.routePlannerTaskId
        };
        const manualRouteInformationList = [
            <ManualRouteInformation>{
                taskIdInfo: task,
                employeeId: employee.employeeId,
                workHourId: employee.workHourId
            }
        ];
        this.routePlanningService.employeeTaskUpdateStateChanged(true);
        this.routesService.sendTaskChangeToMrp(manualRouteInformationList)
            .pipe(
                catchError(() => {
                    transferArrayItem([event.item.data], event.previousContainer.data, 0, event.previousIndex);
                    return throwError('Update routes order failed.');
                }),
                finalize(() => {
                    this.routePlanningService.employeeTaskUpdateStateChanged(false);
                    this.dragDropService.setDraggingDisabled(false);
                })
            )
            .subscribe(result => {
                this.routesService.updateAndVerifyResult(result.routes, event, () => this.setRoutes(result.routes));
                this.loadEmployees(this.newSearchPhrase);
            });
    }

    public taskEntered(event: CdkDragEnter<string[], RouteTaskView>, employee: EmployeeView): void {
        if (employee.dropDisabled) {
            this.dragDropService.resetPlaceholderPositionInDropContainer(event);
        }
    }

    public setRoutes(routes: VisitList): void {
        this.visits = routes.tasks;
        this.keyNumbers = routes.routeKeyNumbers;
        this.reloadRoutes.emit(routes);
    }

    public findBestAll(event: CdkDragDrop<TaskIdInfo[]>): void {
        if (event.isPointerOverContainer) {
            event.previousContainer.data.splice(event.previousIndex, 1);

            const task = <TaskIdInfo>{
                taskId: event.item.data.originalTaskId,
                routePlannerTaskId: event.item.data.routePlannerTaskId
            };
            const manualRouteInformationList = [
                <ManualRouteInformation>{
                    taskIdInfo: task
                }
            ];
            this.routePlanningService.employeeTaskUpdateStateChanged(true);
            this.routesService.sendTaskChangeToMrp(manualRouteInformationList)
                .pipe(
                    catchError(() => {
                        transferArrayItem([event.item.data], event.previousContainer.data, 0, event.previousIndex);
                        return throwError('Update routes order failed.');
                    }),
                    finalize(() => {
                        this.routePlanningService.employeeTaskUpdateStateChanged(false);
                        this.dragDropService.setDraggingDisabled(false);
                    })
                )
                .subscribe(result => {
                    this.routesService.updateAndVerifyResult(result.routes, event, () => this.setRoutes(result.routes));
                });
        }
    }

    public isRideSharingInEmployeeRoute(employee: EmployeeView) {
        return this.keyNumbers
            .filter(keyNumber => keyNumber.employeeId === employee.employeeId && keyNumber.rideSharing)
            .length > 0;
    }

    public switchSwapMode(): void {
        this.swapModeActive = !this.swapModeActive;
    }

    public updateEmployeeSwapSelection(employee: EmployeeView, shouldBeSelected: boolean): void {
        if (shouldBeSelected) {
            if (this.selectedSwapEmployees.size < this.maxSwapEmployees || this.selectedSwapEmployees.has(employee)) {
                this.selectedSwapEmployees.add(employee);
            }
        } else {
            this.selectedSwapEmployees.delete(employee);
        }
    }

    public isDisabledSwap(employee: EmployeeView): boolean {
        return !this.selectedSwapEmployees.has(employee) && this.selectedSwapEmployees.size >= this.maxSwapEmployees ||
            this.selectedSwapEmployees.size === 1 && this.selectedSwapEmployees.values().next().value.shiftType !== employee.shiftType;
    }

    public cancelSwapMode(): void {
        this.swapModeActive = false;
        this.selectedSwapEmployees.clear();
    }

    public handleSwapCellClick(employee: EmployeeView): void {
        const isCurrentlySelected = this.selectedSwapEmployees.has(employee);
        if (!isCurrentlySelected && this.selectedSwapEmployees.size >= this.maxSwapEmployees) {
            return;
        }
        this.updateEmployeeSwapSelection(employee, !isCurrentlySelected);
    }

    public openSwapModal(): void {
        this.modalService.openCustomModal(
            EmployeeSwapModalComponent,
            (routesResult: RoutesResult) => this.updateSwappedRoutes(routesResult),
            null,
            this.selectedSwapEmployees,
            true
        );
    }

    public updateSwappedRoutes(routesResult: RoutesResult): void {
        this.swapModeActive = false;
        this.selectedSwapEmployees.clear();

        this.setRoutes(routesResult.routes);
        this.loadEmployees(this.newSearchPhrase);
        this.navigationService.showResetButtonOnly(true);
    }

    public applyRevertedChanges(routes: VisitList): void {
        this.swapWorklistsFeature
            .pipe(
                take(1),
                untilDestroyed(this),
                tap(value => {
                    if (value) {
                        this.setRoutes(routes);
                        this.employeeService.initEmployees();
                        this.loadEmployees(this.newSearchPhrase);
                    }
                })
            )
            .subscribe();
    }

    private loadEmployees(searchPhrase?: string): void {
        if (!this.keyNumbers) {
            return;
        }
        if (searchPhrase) {
            this.newSearchPhrase = searchPhrase;
        }

        this.employeeService
            .getEmployees(this.sortValue, this.newSearchPhrase, true, false, this.visits)
            .pipe(
                first(),
                map(employees => {
                    employees.forEach(employee => {
                        const keyNumber = this.keyNumbers.find(key => key.workHourId === employee.workHourId);
                        if (keyNumber == null) {
                            employee.lateArrivals = false;
                        } else {
                            employee.lateArrivals =
                                keyNumber.totalTimeBreakingTimeWindowsInSeconds &&
                                keyNumber.totalTimeBreakingTimeWindowsInSeconds > 59;
                        }
                        employee.allocatedTasks = this.visitMapper.mapToTaskIdInfo(this.visits, employee.workHourId);

                        if (this.highlightDelaysEnabled) {
                            if (this.employeeDelays.length > 0) {
                                const employeeDelay =
                                    this.employeeDelays
                                        .find(employeeDelay => employeeDelay.workhourId === employee.workHourId);
                                if (employeeDelay) {
                                    employee.routeDelay = employeeDelay.employeeDelayTotal;
                                }
                            }
                        }
                    });
                    return employees;
                }),
                finalize(() => (this.loading = false))
            )
            .subscribe(employees => {
                this.employees = employees;
                this.displayEmployees = employees;
                this.selectFirstEmployee();
            });
    }

    private selectFirstEmployee(): void {
        if (this.displayEmployees && this.displayEmployees.length > 0) {
            const selectedEmployee = this.displayEmployees.find(employee => employee.selected);
            if (!selectedEmployee) {
                this.displayEmployees[0].selected = true;
            }
            this.select.emit(selectedEmployee ?? this.displayEmployees[0]);
            this.activeEmployeeService.setActiveEmployee(selectedEmployee ?? this.displayEmployees[0]);
        }
    }

    private deselectEmployee(): void {
        const employee = this.displayEmployees?.find(i => i.selected);
        if (employee) {
            employee.selected = false;
        }
    }

    private estimateSyncTaskAllocation() {
        this.routesService.getLocalRoutes()
            .pipe(
                untilDestroyed(this),
                take(1),
                tap(routes => {
                    if (this.employees) {
                        if (routes && routes.routes.tasks.length > 0) {
                            const unpairedSyncTasks
                                = this.routeTaskService.estimateUnpairedRouteSyncTasks(routes.routes.tasks);
                            const pairedUnallignedTasks: Visit[] = [];

                            const pairedSyncTasks =
                                this.routeTaskService.estimateRouteSyncTaskPairs(routes.routes.tasks);
                            pairedSyncTasks.forEach(pairedSyncTask => {
                                const syncedTaskPair = pairedSyncTasks.find(otherSyncedTask =>
                                    pairedSyncTask !== otherSyncedTask &&
                                    pairedSyncTask.taskId === otherSyncedTask.taskId &&
                                    pairedSyncTask.locationName === otherSyncedTask.locationName
                                );
                                if (syncedTaskPair.estimatedEndTime !== pairedSyncTask.estimatedEndTime &&
                                    syncedTaskPair.estimatedStartTime !== pairedSyncTask.estimatedStartTime) {
                                    pairedUnallignedTasks.push(syncedTaskPair);
                                }
                            });

                            this.employees.forEach(employee => {
                                employee.hasUnpairedSyncTask =
                                    unpairedSyncTasks.some(task => task.employeeId === employee.employeeId);
                                employee.hasUnsyncedTimeTask =
                                    pairedUnallignedTasks.some(task => task.employeeId === employee.employeeId);
                            });
                            this.changeDetectorRef.detectChanges();
                        }
                    }
                })
            ).subscribe();
    }
}
